import { useToggle } from 'usehooks-ts'
import { useCallback, useRef, useState } from 'react'
import { asyncDelay } from '@weblite/logic-toolkit'
import {
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  Typography,
  darken,
  styled,
} from '@mui/material'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import KeyboardVoiceRoundedIcon from '@mui/icons-material/KeyboardVoiceRounded'
import { ClearIcon, SendIcon } from '@weblite/icons'
import { useHistoryCustomBack } from 'weblite-router'
import { RecordedVoice } from '@weblite-wapps/protocol'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { t } from '$i18n'
import { nothing } from '$helper/functions/utils'
import { BottomDrawer } from '$common/bottom-drawer'
import { TextEditor } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import { VoicePreview } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/voice-preview'
import { showEmojiPickerPopover } from '../emoji-picker'
import { ConfirmationDialog } from '../confirmation-dialog'
import { useReactRecorder } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/recorder/recorder.react-hook'
import { RecorderStatus } from '$comp/_app/components/chat/components/footer/components/send-field/send-field.type'
import { Timer } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/components/timer'
import { durationLabelRenderer } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/components/durationLabelRenderer'

const editorId = 'send-voice-dialog'

interface Props {
  recordedVoice?: RecordedVoice
  onConfirm: (recordedVoice: RecordedVoice, caption?: string) => unknown
  onCancel: () => unknown
}

export function SendVoiceDialog({
  recordedVoice: recordedVoiceProp,
  onConfirm,
  onCancel,
}: Props) {
  const recordingNeededRef = useRef(!recordedVoiceProp)
  const [recorderState, setRecorderState] = useState<RecorderStatus>('IDLE')
  const [recordedVoice, setRecordedVoice] = useState<RecordedVoice | undefined>(
    recordedVoiceProp,
  )

  const { isPending: isWaiting } = useReactRecorder({
    state: recorderState,
    setRecorderState,
    setRecorderVoice: setRecordedVoice,
  })

  const isRecording =
    recorderState === 'RECORD' ||
    recorderState === 'STARTING' ||
    recorderState === 'RESTARTING' ||
    recorderState === 'PREVIEW'

  const [caption, setCaption] = useState('')

  const [isConfirmingClose, toggleIsConfirmingClose] = useToggle(false)

  const [isConfirmingRestart, toggleIsConfirmingRestart] = useToggle(false)

  const handleClose = useCallback(async () => {
    closeCurrentModal()
    await asyncDelay(300)
    onCancel()
  }, [onCancel])

  useHistoryCustomBack(() => {
    if (recordedVoice) toggleIsConfirmingClose()
    else closeCurrentModal()
  }, [toggleIsConfirmingClose, recordedVoice])

  const handleConfirm = useCallback(async () => {
    if (!recordedVoice) return
    await closeCurrentModal()
    onConfirm(recordedVoice, caption)
  }, [caption, onConfirm, recordedVoice])

  const handleMainAction = useCallback(() => {
    if (isRecording) return setRecorderState('PREVIEW')
    setRecorderState('STARTING')
  }, [isRecording, setRecorderState])

  const handleRestartRecording = useCallback(() => {
    setRecorderState('RESTARTING')
    toggleIsConfirmingRestart()
  }, [toggleIsConfirmingRestart])

  return (
    <>
      <BottomDrawer
        isOpen
        onClose={() => {
          if (isRecording || recordedVoice) toggleIsConfirmingClose()
          else handleClose()
        }}
      >
        {!recordedVoice && (
          <RecordContainer>
            {isRecording ? (
              <Typography variant="h3">
                {t('chat.sendVoiceDialog.record.recording')}
              </Typography>
            ) : (
              <Typography variant="h3">
                {t('chat.sendVoiceDialog.record.title')}
              </Typography>
            )}
            <IconContainer
              onClick={handleMainAction}
              sx={{
                pointerEvents: isWaiting ? 'none' : 'auto',
              }}
              disabled={isWaiting}
            >
              {isWaiting ? (
                <CircularProgress size={44} color="inherit" />
              ) : isRecording ? (
                <SendRoundedIcon sx={{ width: '44px', height: '44px' }} />
              ) : (
                <KeyboardVoiceRoundedIcon
                  sx={{ width: '52px', height: '52px' }}
                />
              )}
            </IconContainer>

            {(recorderState === 'RECORD' || recorderState === 'WAITING') && (
              <Timer
                renderer={durationLabelRenderer}
                interval={100}
                stop={recorderState === 'WAITING'}
              />
            )}

            <RecordActions>
              <Button
                variant="text"
                sx={{ color: 'secondary500' }}
                disabled={!isRecording || isWaiting}
                onClick={toggleIsConfirmingRestart}
              >
                {t('chat.sendVoiceDialog.record.restart')}
              </Button>
              <Button
                variant="text"
                sx={{ color: 'secondary500' }}
                onClick={() => {
                  if (isRecording) toggleIsConfirmingClose()
                  else handleClose()
                }}
              >
                {t('chat.sendVoiceDialog.record.cancel')}
              </Button>
            </RecordActions>
          </RecordContainer>
        )}
        {recordedVoice && (
          <Column>
            <Header>
              <IconButton onClick={toggleIsConfirmingClose}>
                <ClearIcon sx={{ color: 'neutral700' }} />
              </IconButton>
              <Typography>{t('chat.sendVoiceDialog.title')}</Typography>
            </Header>
            <PreviewWrapper>
              <VoicePreview
                recorderVoice={recordedVoice}
                onReset={handleClose}
                onSend={nothing}
                showSendButton={false}
              />
            </PreviewWrapper>
            <SendFieldWrapper>
              <IconButton onClick={handleConfirm}>
                <SendIcon sx={{ color: 'primary500' }} />
              </IconButton>

              {!recordingNeededRef.current && (
                <TextEditor
                  action={undefined}
                  mentionIsDisabled={false}
                  darkMode={false}
                  placeholder={t('chat.sendField')}
                  setHasMessage={nothing}
                  onContentChange={setCaption}
                  setMentionQuery={nothing}
                  onSubmit={handleConfirm}
                  onPasteFile={nothing}
                  sx={{
                    minHeight: '40px!important',
                    boxSizing: 'border-box',
                    marginLeft: 0,
                  }}
                  editorId={editorId}
                  onClickEmojiIcon={e => {
                    e.stopPropagation()
                    showEmojiPickerPopover({
                      anchorEl: e.currentTarget,
                      editorId,
                    })
                  }}
                />
              )}
            </SendFieldWrapper>
          </Column>
        )}
      </BottomDrawer>
      <ConfirmationDialog
        isOpen={isConfirmingClose}
        title={t(`confirmDialog.discardVoiceMessage.title`)}
        description={t(`confirmDialog.discardVoiceMessage.description`)}
        confirmOption={t('common.remove')}
        cancelOption={t('common.cancel')}
        alertMode
        onCancel={toggleIsConfirmingClose}
        onConfirm={handleClose}
      />
      <ConfirmationDialog
        isOpen={isConfirmingRestart}
        title={t(`confirmDialog.restartRecordingVoice.title`)}
        description={t(`confirmDialog.restartRecordingVoice.description`)}
        confirmOption={t('common.remove')}
        cancelOption={t('common.cancel')}
        alertMode
        onCancel={toggleIsConfirmingRestart}
        onConfirm={handleRestartRecording}
      />
    </>
  )
}

const Header = styled('div')`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  align-items: center;
  height: 64px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral300};
  padding: 12px 8px;
`

const Column = styled('div')`
  display: flex;
  flex-direction: column;
  width: 400px;
`

const PreviewWrapper = styled('div')`
  width: 100%;
`

const SendFieldWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid ${({ theme }) => theme.palette.neutral300};
  padding: 12px;
  width: 100%;
`

const RecordContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px 10px;
`

const IconContainer = styled(ButtonBase)`
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.primary500};
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${({ theme }) => darken(theme.palette.primary500, 0.1)};
  }
  color: ${({ theme }) => theme.palette.neutral000};
  cursor: pointer;

  & > svg {
    color: inherit;
  }
`

const RecordActions = styled('div')`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`
